<template>
  <a
    :href="component.link"
    class="inline-block text-center no-underline rounded-md uppercase font-bold tracking-wider"
    :class="[
      buttonClass.trim() ? buttonClass.trim().split(/\s+/) : [],
      imageOnly
        ? {}
        : {
            'px-4 py-2': sizeSmall,
            'px-6 py-3': sizeMedium,
            'px-8 py-4': sizeLarge,
            'bg-brand text-onyx': typePrimary,
            'bg-charcoal text-mouse': typeSecondary,
            'bg-success text-onyx': typeSuccess,
            'bg-danger text-mouse': typeDanger,
            'border-2 border-brand text-brand': typeDefault,
          },
    ]"
    :target="component.newTab ? '_blank' : '_self'"
  >
    <strapi-image
      v-if="component.image"
      :image="component.image"
      :alt="component.text"
      :force-width="forceImageWidth"
      :force-height="forceImageHeight"
      :class="imageClasses"
    />
    <span v-else>
      {{ component.text }}
    </span>

    <slot></slot>
  </a>
</template>

<script setup lang="ts">
import type { StrapiButton } from '~/apollo/types/types';

const props = withDefaults(
  defineProps<{
    component: StrapiButton;
    size?: 'small' | 'sm' | 'medium' | 'md' | 'large' | 'lg';
    buttonClass?: string;
    forceImageWidth?: number;
    forceImageHeight?: number;
    imageClasses?: string | string[] | Record<string, boolean>;
  }>(),
  {
    size: 'large',
    buttonClass: '',
    forceImageWidth: undefined,
    forceImageHeight: undefined,
    imageClasses: () => [],
  },
);

const imageOnly = computed(() => Boolean(props.component.image));

const sizeSmall = computed(
  () => !imageOnly.value && ['small', 'sm'].includes(props.size),
);
const sizeMedium = computed(
  () => !imageOnly.value && ['medium', 'md'].includes(props.size),
);
const sizeLarge = computed(
  () =>
    !imageOnly.value && !['small', 'sm', 'medium', 'md'].includes(props.size),
);

const typePrimary = computed(
  () => !imageOnly.value && props.component.type === 'primary',
);
const typeSecondary = computed(
  () => !imageOnly.value && props.component.type === 'secondary',
);
const typeSuccess = computed(
  () => !imageOnly.value && props.component.type === 'success',
);
const typeDanger = computed(
  () => !imageOnly.value && props.component.type === 'danger',
);
const typeDefault = computed(() => {
  const t = props.component.type;
  return (
    !imageOnly.value &&
    !['primary', 'secondary', 'success', 'danger'].includes(t)
  );
});
</script>
